import React from "react";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz, type ColDef } from "ag-grid-community";

import { IStripeInvoice } from "@eagerdog/stripe";

import styles from "./InvoicesGrid.module.scss";

interface IProps {
  invoices: IStripeInvoice[]
}

const InvoicesGrid: React.FC<IProps> = ({ invoices }) => {
  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto"
  });

  const capitalize = (s: string): string => {
    if (!s) return "";

    return s[0].toUpperCase() + s.slice(1);
  }

  const formatBillingReason = (reason: string): string => {
    const reasonMap: Record<string, string> = {
      'subscription_create': 'New Subscription',
      'subscription_cycle': 'Recurring',
      'subscription_update': 'Subscription Updated',
      'subscription_threshold': 'Threshold Reached',
      'manual': 'Manual',
      'upcoming': 'Upcoming',
    };
    return reasonMap[reason] || capitalize(reason.replace(/_/g, ' '));
  }

  const LinkRenderer = (props: any) => {
    if (!props.value) return null;

    return (
      <a
        href={props.value}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        View Invoice
      </a>
    );
  };

  const cols: ColDef[] = [
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      filter: true,
      valueFormatter: ({ value }) => capitalize(value.replace("_", " ")),
    },
    {
      field: "reason",
      headerName: "Reason",
      minWidth: 160,
      filter: true,
      valueFormatter: ({ value }) => formatBillingReason(value),
    },

    {
      field: "created",
      headerName: "Created At",
      minWidth: 160,
      filter: true,
      valueFormatter: ({ value }) =>
        value ? new Date(value * 1000).toLocaleDateString() : "-",
    },
    {
      field: "paidAt",
      headerName: "Paid At",
      minWidth: 160,
      filter: true,
      valueFormatter: ({ value }) =>
        value ? new Date(value * 1000).toLocaleDateString() : "-",
    },

    {
      field: "amountDue",
      headerName: "Amount Due",
      minWidth: 160,
      filter: true,
    },
    {
      field: "amountPaid",
      headerName: "Amount Paid",
      minWidth: 160,
      filter: true,
    },
    {
      field: "total",
      headerName: "Total",
      minWidth: 160,
      filter: true,
    },

    {
      field: "attemptCount",
      headerName: "Attempts",
      minWidth: 140,
      filter: true,
    },
    {
      field: "nextAttempt",
      headerName: "Next Attempt",
      minWidth: 160,
      filter: true,
      valueFormatter: ({ value }) =>
        value ? new Date(value * 1000).toLocaleDateString() : "-",
    },
    {
      field: "number",
      headerName: "Number",
      minWidth: 160,
      filter: true,
    },
    {
      field: "url",
      headerName: "URL",
      minWidth: 140,
      filter: true,
      cellRenderer: 'linkRenderer'
    },
  ];

  const buildRowData = () => {
    return invoices.map((invoice) => {
      const formatter = new Intl.NumberFormat('en-US', {
        style: "currency",
        currency: invoice.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return {
        status: invoice.status,
        reason: invoice.billing_reason,
        created: invoice.created,
        // dueDate: invoice.due_date,
        paidAt: invoice.status_transitions.paid_at,
        // periodStart: new Date(invoice.period_start * 1000),
        // periodEnd: new Date(invoice.period_end * 1000),
        amountPaid: formatter.format(invoice.amount_paid / 100),
        amountDue: formatter.format(invoice.amount_due / 100),
        total: formatter.format(invoice.total / 100),
        attemptCount: invoice.attempt_count,
        nextAttempt: invoice.next_payment_attempt,
        number: invoice.number,
        url: invoice.hosted_invoice_url,
      }
      // amount_remaining
      // default_payment_method
      // last_finalization_error
    });
  }

  return (
    <div className={styles.tableWrap}>
      <AgGridReact
        suppressRowTransform
        pagination
        columnDefs={cols}
        rowData={buildRowData()}
        components={{ linkRenderer: LinkRenderer }}
        defaultColDef={{ flex: 1 }}
        theme={theme}
        paginationPageSize={20}
        initialState={{
          sort: {
            sortModel: [{ colId: "created", sort: "asc" }]
          }
        }}
      />
    </div>
  )
}

export { InvoicesGrid }
